/**
 * JourneyOverlayProvider — DRR-309 Phase 9
 *
 * Context provider for overlay pattern composition.
 * Manages which overlays are active and their stacking order.
 *
 * Three-tier stacking:
 * - Layer 3 (Blocking): EmergencyOverride, WitnessVerification
 * - Layer 2 (Persistent): OfflineStatus, EscalationTimer, etc.
 * - Layer 1 (Contextual): CollaborationIndicator, AIAssistant
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import type {
  PatternOverlay,
  OverlayPriority,
  JourneyPatternState,
  StalenessWarning,
  EscalationTimerState,
  WitnessRecord,
  BreakGlassJustification,
  ShiftHandoffState,
  CollaborationPresence,
  AISuggestion,
  RegulatoryDeadline,
} from './types';
import { OVERLAY_PRIORITIES } from './types';

// ============================================================================
// GraphQL Mutation
// ============================================================================

const CREATE_JOURNEY_AUDIT_EVENT = gql`
  mutation CreateJourneyAuditEvent($tenantId: String!, $input: GqlCreateJourneyAuditEventInput!) {
    createJourneyAuditEvent(tenantId: $tenantId, input: $input) {
      id
      instanceId
      eventType
      reason
      reviewTaskId
      timestamp
      triggeredBy
    }
  }
`;

// ============================================================================
// Context Type
// ============================================================================

interface JourneyOverlayContextType {
  // Pattern state
  patternState: JourneyPatternState;
  activeOverlays: PatternOverlay[];
  hasBlockingOverlay: boolean;

  // Overlay activation
  activateOverlay: (
    overlay: PatternOverlay,
    props?: Record<string, unknown>
  ) => void;
  deactivateOverlay: (overlay: PatternOverlay) => void;
  isOverlayActive: (overlay: PatternOverlay) => boolean;

  // Life-safety: Emergency override
  requestEmergencyOverride: (
    justification: BreakGlassJustification
  ) => Promise<boolean>;
  cancelEmergencyOverride: () => void;
  emergencyJustification: BreakGlassJustification | null;

  // Life-safety: Witness verification
  requestWitness: (
    action: string,
    requiredRole: string,
    minWitnesses?: number
  ) => Promise<boolean>;
  addWitness: (witness: WitnessRecord) => void;
  clearWitnesses: () => void;
  witnesses: WitnessRecord[];
  pendingWitnessAction: string | null;

  // Offline status
  stalenessWarnings: StalenessWarning[];
  setStalenessWarnings: (warnings: StalenessWarning[]) => void;
  isOffline: boolean;
  setIsOffline: (offline: boolean) => void;
  pendingSyncCount: number;
  setPendingSyncCount: (count: number) => void;

  // Escalation
  escalationTimer: EscalationTimerState | null;
  setEscalationTimer: (timer: EscalationTimerState | null) => void;

  // Regulatory deadlines
  deadlines: RegulatoryDeadline[];
  setDeadlines: (deadlines: RegulatoryDeadline[]) => void;

  // Shift handoff
  shiftHandoff: ShiftHandoffState | null;
  setShiftHandoff: (handoff: ShiftHandoffState | null) => void;

  // Collaboration
  collaborators: CollaborationPresence[];
  setCollaborators: (collaborators: CollaborationPresence[]) => void;

  // AI Assistant
  aiSuggestions: AISuggestion[];
  setAiSuggestions: (suggestions: AISuggestion[]) => void;
  dismissSuggestion: (id: string) => void;
}

const JourneyOverlayContext = createContext<JourneyOverlayContextType | null>(
  null
);

// ============================================================================
// Provider Props
// ============================================================================

interface JourneyOverlayProviderProps {
  readonly children: ReactNode;
  /** Tenant ID for mutations */
  readonly tenantId?: string;
  /** Instance ID for context */
  readonly instanceId?: string;
  /** Default overlays to enable */
  readonly defaultOverlays?: PatternOverlay[];
  /** Enable life-safety features (emergency override, witness) */
  readonly lifeSafety?: boolean;
  /** Base UI pattern from workflow state */
  readonly basePattern?: string;
}

// ============================================================================
// Provider Implementation
// ============================================================================

export function JourneyOverlayProvider({
  children,
  tenantId,
  instanceId,
  defaultOverlays = [],
  lifeSafety = true,
  basePattern = 'StatusOnly',
}: JourneyOverlayProviderProps) {
  // Audit event mutation for life-safety actions
  const [createAuditEvent] = useMutation(CREATE_JOURNEY_AUDIT_EVENT);
  // Active overlays
  const [activeOverlays, setActiveOverlays] =
    useState<PatternOverlay[]>(defaultOverlays);
  const [overlayProps, setOverlayProps] = useState<
    Map<PatternOverlay, Record<string, unknown>>
  >(new Map());

  // Emergency override state
  const [emergencyActive, setEmergencyActive] = useState(false);
  const [emergencyJustification, setEmergencyJustification] =
    useState<BreakGlassJustification | null>(null);

  // Witness verification state
  const [witnesses, setWitnesses] = useState<WitnessRecord[]>([]);
  const [pendingWitnessAction, setPendingWitnessAction] = useState<
    string | null
  >(null);
  const [witnessResolve, setWitnessResolve] = useState<
    ((value: boolean) => void) | null
  >(null);

  // Offline status
  const [stalenessWarnings, setStalenessWarnings] = useState<StalenessWarning[]>(
    []
  );
  const [isOffline, setIsOffline] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  // Escalation
  const [escalationTimer, setEscalationTimer] =
    useState<EscalationTimerState | null>(null);

  // Regulatory deadlines
  const [deadlines, setDeadlines] = useState<RegulatoryDeadline[]>([]);

  // Shift handoff
  const [shiftHandoff, setShiftHandoff] = useState<ShiftHandoffState | null>(
    null
  );

  // Collaboration
  const [collaborators, setCollaborators] = useState<CollaborationPresence[]>(
    []
  );

  // AI suggestions
  const [aiSuggestions, setAiSuggestions] = useState<AISuggestion[]>([]);

  // Sort overlays by priority (blocking first)
  const sortedOverlays = useMemo(() => {
    const priorityOrder: Record<OverlayPriority, number> = {
      blocking: 3,
      persistent: 2,
      contextual: 1,
    };
    return [...activeOverlays].sort(
      (a, b) =>
        priorityOrder[OVERLAY_PRIORITIES[b]] -
        priorityOrder[OVERLAY_PRIORITIES[a]]
    );
  }, [activeOverlays]);

  // Check if any blocking overlay is active
  const hasBlockingOverlay = useMemo(
    () =>
      sortedOverlays.some(
        (overlay) => OVERLAY_PRIORITIES[overlay] === 'blocking'
      ),
    [sortedOverlays]
  );

  // Pattern state
  const patternState: JourneyPatternState = useMemo(
    () => ({
      basePattern,
      activeOverlays: sortedOverlays,
      emergencyActive,
      offlineStale: stalenessWarnings.some((w) => w.action === 'block'),
      escalationLevel: escalationTimer?.currentLevel ?? 0,
      witnessPending: pendingWitnessAction !== null,
    }),
    [
      basePattern,
      sortedOverlays,
      emergencyActive,
      stalenessWarnings,
      escalationTimer,
      pendingWitnessAction,
    ]
  );

  // Activate an overlay
  const activateOverlay = useCallback(
    (overlay: PatternOverlay, props?: Record<string, unknown>) => {
      setActiveOverlays((prev) => {
        if (prev.includes(overlay)) return prev;
        return [...prev, overlay];
      });
      if (props) {
        setOverlayProps((prev) => new Map(prev).set(overlay, props));
      }
    },
    []
  );

  // Deactivate an overlay
  const deactivateOverlay = useCallback((overlay: PatternOverlay) => {
    setActiveOverlays((prev) => prev.filter((o) => o !== overlay));
    setOverlayProps((prev) => {
      const next = new Map(prev);
      next.delete(overlay);
      return next;
    });
  }, []);

  // Check if overlay is active
  const isOverlayActive = useCallback(
    (overlay: PatternOverlay) => activeOverlays.includes(overlay),
    [activeOverlays]
  );

  // Request emergency override (break-glass)
  const requestEmergencyOverride = useCallback(
    async (justification: BreakGlassJustification): Promise<boolean> => {
      if (!lifeSafety) {
        console.warn('Life-safety features disabled');
        return false;
      }

      // Send audit event to backend and create 24-hour review task
      if (tenantId && instanceId) {
        try {
          await createAuditEvent({
            variables: {
              tenantId,
              input: {
                instanceId,
                eventType: 'emergency_override',
                reason: justification.reason,
                createReviewTask: true, // Creates 24-hour review task
              },
            },
          });
        } catch (error) {
          console.error('Failed to record emergency override audit event:', error);
          // Continue with override even if audit fails - life-safety takes priority
        }
      }

      setEmergencyJustification(justification);
      setEmergencyActive(true);
      activateOverlay('EmergencyOverride');

      return true;
    },
    [lifeSafety, activateOverlay, tenantId, instanceId, createAuditEvent]
  );

  // Cancel emergency override
  const cancelEmergencyOverride = useCallback(() => {
    setEmergencyActive(false);
    setEmergencyJustification(null);
    deactivateOverlay('EmergencyOverride');
  }, [deactivateOverlay]);

  // Request witness verification
  const requestWitness = useCallback(
    async (
      action: string,
      requiredRole: string,
      minWitnesses = 1
    ): Promise<boolean> => {
      if (!lifeSafety) {
        console.warn('Life-safety features disabled');
        return true; // Allow if life-safety disabled
      }

      // Check if we already have valid witnesses
      const validWitnesses = witnesses.filter((w) => !w.isExpired);
      if (validWitnesses.length >= minWitnesses) {
        return true;
      }

      // Show witness verification overlay
      setPendingWitnessAction(action);
      activateOverlay('WitnessVerification', {
        action,
        requiredRole,
        minWitnesses,
        currentWitnesses: validWitnesses.length,
      });

      // Wait for witnesses to be added
      return new Promise((resolve) => {
        setWitnessResolve(() => resolve);
      });
    },
    [lifeSafety, witnesses, activateOverlay]
  );

  // Add a witness
  const addWitness = useCallback(
    (witness: WitnessRecord) => {
      setWitnesses((prev) => [...prev, witness]);

      // Check if we have enough witnesses now
      const overlayConfig = overlayProps.get('WitnessVerification');
      const minWitnesses = (overlayConfig?.minWitnesses as number) ?? 1;
      const newTotal = witnesses.filter((w) => !w.isExpired).length + 1;

      if (newTotal >= minWitnesses && witnessResolve) {
        witnessResolve(true);
        setWitnessResolve(null);
        setPendingWitnessAction(null);
        deactivateOverlay('WitnessVerification');
      }
    },
    [witnesses, overlayProps, witnessResolve, deactivateOverlay]
  );

  // Clear witnesses
  const clearWitnesses = useCallback(() => {
    setWitnesses([]);
    if (witnessResolve) {
      witnessResolve(false);
      setWitnessResolve(null);
    }
    setPendingWitnessAction(null);
    deactivateOverlay('WitnessVerification');
  }, [witnessResolve, deactivateOverlay]);

  // Dismiss AI suggestion
  const dismissSuggestion = useCallback((id: string) => {
    setAiSuggestions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Context value
  const value: JourneyOverlayContextType = useMemo(
    () => ({
      patternState,
      activeOverlays: sortedOverlays,
      hasBlockingOverlay,

      activateOverlay,
      deactivateOverlay,
      isOverlayActive,

      requestEmergencyOverride,
      cancelEmergencyOverride,
      emergencyJustification,

      requestWitness,
      addWitness,
      clearWitnesses,
      witnesses,
      pendingWitnessAction,

      stalenessWarnings,
      setStalenessWarnings,
      isOffline,
      setIsOffline,
      pendingSyncCount,
      setPendingSyncCount,

      escalationTimer,
      setEscalationTimer,

      deadlines,
      setDeadlines,

      shiftHandoff,
      setShiftHandoff,

      collaborators,
      setCollaborators,

      aiSuggestions,
      setAiSuggestions,
      dismissSuggestion,
    }),
    [
      patternState,
      sortedOverlays,
      hasBlockingOverlay,
      activateOverlay,
      deactivateOverlay,
      isOverlayActive,
      requestEmergencyOverride,
      cancelEmergencyOverride,
      emergencyJustification,
      requestWitness,
      addWitness,
      clearWitnesses,
      witnesses,
      pendingWitnessAction,
      stalenessWarnings,
      isOffline,
      pendingSyncCount,
      escalationTimer,
      deadlines,
      shiftHandoff,
      collaborators,
      aiSuggestions,
      dismissSuggestion,
    ]
  );

  return (
    <JourneyOverlayContext.Provider value={value}>
      {children}
    </JourneyOverlayContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useJourneyOverlay(): JourneyOverlayContextType {
  const context = useContext(JourneyOverlayContext);
  if (!context) {
    throw new Error(
      'useJourneyOverlay must be used within a JourneyOverlayProvider'
    );
  }
  return context;
}

export default JourneyOverlayProvider;
